import { useQuery } from '@tanstack/react-query';
import { DatePicker as AntDatePicker } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import commaNumber from 'comma-number';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Badge } from '@/components/Badge/Badge';
import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import { feedbackCountQueryOptions } from '@/entities/feedback';
import { useFeedbackTerminology } from '@/entities/workspace';
import { FeedbackExport } from '@/features/Feedback/components/FeedbackExport/FeedbackExport';
import useColor from '@/hooks/domain/useColor';
import service from '@/services';
import { modalStyle } from '@/styles/style';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';
import { getTimezoneDayjs } from '@/utils/time';

type Props = {
  taxonomyAlias: string;
  topicAlias?: string;
  title: string;
};

const TaxonomyExportModal = (props: Props) => {
  const { closeModal } = useModalContext();

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    getTimezoneDayjs().subtractVal(30, 'day'),
    getTimezoneDayjs(),
  ]);

  const handleDateChange: RangePickerProps['onChange'] = (dates) => {
    setDateRange((prev) => {
      return [dates?.[0] || prev[0], dates?.[1] || prev[1]];
    });
  };

  const handleConfirm = async (reason?: string, password?: string) => {
    await service.taxonomy.requestExportTaxonomyBlock(props.taxonomyAlias, {
      filter: {
        [FEEDBACK_COLUMNS.DATE_START]: dateRange[0].toJSON(),
        [FEEDBACK_COLUMNS.DATE_END]: dateRange[1].toJSON(),
      },
      password: password,
      reason: reason,
      topic_id: props.topicAlias,
    });
    closeModal();
  };

  const filter: StructuredFilter = useMemo(
    () =>
      props.topicAlias
        ? {
            [FEEDBACK_COLUMNS.TAXONOMY_TOPIC]: [
              {
                op: STRUCTURED_FILTER_OPERATOR.OR,
                values: [props.topicAlias],
              },
            ],
            [FEEDBACK_COLUMNS.DATE_START]: dateRange[0].toJSON(),
            [FEEDBACK_COLUMNS.DATE_END]: dateRange[1].toJSON(),
          }
        : {
            [FEEDBACK_COLUMNS.TAXONOMY]: [
              {
                op: STRUCTURED_FILTER_OPERATOR.OR,
                values: [props.taxonomyAlias],
              },
            ],
            [FEEDBACK_COLUMNS.DATE_START]: dateRange[0].toJSON(),
            [FEEDBACK_COLUMNS.DATE_END]: dateRange[1].toJSON(),
          },
    [props.topicAlias, props.taxonomyAlias, dateRange],
  );

  const { data: count } = useQuery(feedbackCountQueryOptions(filter));

  const totalFeedback = count || 0;

  return (
    <TaxonomyExportModalContent
      dateRange={dateRange}
      handleDateChange={handleDateChange}
      totalFeedback={totalFeedback}
      handleConfirm={handleConfirm}
      closeModal={closeModal}
      badgeName={props.title}
    />
  );
};

type ContentProps = {
  badgeName: string;
  dateRange: [Dayjs, Dayjs];
  handleDateChange: RangePickerProps['onChange'];
  totalFeedback: number;
  handleConfirm: (reason?: string, password?: string) => void;
  closeModal: () => void;
};

export const TaxonomyExportModalContent = (props: ContentProps) => {
  const colors = useColor();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();

  return (
    <Container>
      <VFlex g={32}>
        <VFlex g={16}>
          <VFlex g={16}>
            <Flex j="space-between" a="center">
              <Heading type="h2" color={colors.gray900}>
                Export to csv
              </Heading>
              <Badge>{props.badgeName}</Badge>
            </Flex>

            <Typo type="body2" color={colors.gray700}>
              {replaceFeedbackTerminology(
                'The CSV file will be generated based on the filtered list and will include Topic, Feedback, and Feedback metadata.',
              )}
            </Typo>
            <AntDatePicker.RangePicker
              value={props.dateRange}
              onChange={props.handleDateChange}
              style={{ width: 352 }}
            />
          </VFlex>
        </VFlex>

        <InfoBox>
          <Flex g={16}>
            <Typo type="body1">
              {replaceFeedbackTerminology('Total feedback')}
            </Typo>
            <Typo type="body1" weight="sb">
              {commaNumber(props.totalFeedback)}
            </Typo>
          </Flex>
        </InfoBox>

        <VFlex g={10}>
          <Typo type="body1" weight="m">
            Notice
          </Typo>
          <Typo type="body2" color={colors.gray700}>
            If the dataset is large, it will be split into multiple files for
            download.
          </Typo>
        </VFlex>

        <Flex g={12} j="flex-end">
          <Button sizeType="m" variant="secondary" onClick={props.closeModal}>
            Cancel
          </Button>
          <FeedbackExport onExportClick={props.handleConfirm}>
            <Button sizeType="m">Confirm</Button>
          </FeedbackExport>
        </Flex>
      </VFlex>
    </Container>
  );
};

const Container = styled.div`
  width: 600px;
  padding: 32px;
  background: ${({ theme }) => theme.colors.FL100};
  ${modalStyle};
`;

const InfoBox = styled.div`
  padding: 20px;
  background: ${({ theme }) => theme.colors.ND04};
  border-radius: 8px;
`;

export default TaxonomyExportModal;
